import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import {
  MAX_SWITCH_REASON_LENGTH,
  type RoutingConfigSummaryRecord,
  type RoutingEmergencyOverrideRecord,
} from '@nadoutong/contracts';
import {
  useDisengageRoutingEmergencyOverride,
  useEngageRoutingEmergencyOverride,
  useRoutingEmergencyOverride,
  useRunRoutingConfigPreflight,
} from '../../api/useRoutingDr.ts';
import { OVERRIDE_EXPIRY_HOURS_OPTIONS } from '../../api/routingDr.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  FormField,
  Panel,
  Select,
  Textarea,
} from '../../ui/index.ts';
import { ROUTING_CONVERGENCE_WORST_CASE_SECONDS } from './convergence.ts';

/**
 * The emergency routing override (#1422).
 *
 * Deliberately **not** styled as another row in the DR plan list, and
 * deliberately not adjacent to the ordinary global-switch button. This control
 * ignores org scoping by design: engaging routes every tenant through one
 * config, including the orgs whose `exclusive` org-scoped assignment makes
 * them unreachable by a global switch. That blast radius is the feature, and a
 * control that looked like the neighbouring one would be the wrong shape for
 * it.
 *
 * Three things follow from that and are load-bearing rather than decorative:
 *
 *  - the engaged state renders as a `bad`-toned banner at the top of the
 *    routing page, not as a status column somebody has to go looking for;
 *  - engaging is two steps — fill the form, then a type-to-confirm dialog
 *    naming the config — because there is no smaller unit to undo;
 *  - the panel keys "in force" on `effective`, never on `engaged`. They differ
 *    once the override passes its expiry, and an expired override that still
 *    read as protecting the fleet is the failure this whole surface exists to
 *    make impossible.
 */

interface EngageDraft {
  readonly config_id: string;
  readonly reason: string;
  /** Hours, as a Select value; `''` means no expiry. */
  readonly expiry_hours: string;
  readonly preflight_token: string | null;
}

const EMPTY_DRAFT: EngageDraft = {
  config_id: '',
  reason: '',
  expiry_hours: String(OVERRIDE_EXPIRY_HOURS_OPTIONS[0]),
  preflight_token: null,
};

/**
 * The chosen duration, in hours, or `undefined` for "no expiry".
 *
 * Deliberately *not* an instant. Computing `Date.now() + hours` here would
 * stamp the browser's clock onto a value the gateway compares against the
 * database's — and `convergence.ts` in this same branch establishes at length
 * that ops-workstation skew is unbounded, refusing to subtract against
 * `Date.now()` for exactly this reason. A workstation two hours slow that
 * picked "2 hours" from the dropdown would send an already-past instant and be
 * told `INVALID_PAYLOAD` with nothing on screen explaining why. The gateway
 * stamps it instead.
 */
function expiresInHours(hours: string): number | undefined {
  const parsed = Number.parseInt(hours, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

/**
 * What the operator is told the override is doing right now.
 *
 * `expired` and `archived_target` are separate states rather than both
 * collapsing into "off": in each case the row still says engaged while the
 * request path has stopped honouring it, and the next responder needs to see
 * that discrepancy rather than a clean "not engaged". They are also separate
 * from each other, because the remedies differ — an expiry was the operator's
 * own choice landing as designed, an archived target is someone having
 * retired the config the fleet was pinned to.
 *
 * The derivation reads the server's two flags rather than recomputing them:
 * `expires_at` is compared against the database's clock, not the browser's,
 * and config status is not on the wire at all.
 */
type OverrideStatus = 'off' | 'in_force' | 'expired' | 'archived_target';

export function overrideStatus(record: RoutingEmergencyOverrideRecord): OverrideStatus {
  if (record.effective) return 'in_force';
  if (!record.engaged) return 'off';
  // Archival is checked first: a config retired *and* past its expiry is more
  // urgently the former, because the expiry was intended and the archival was
  // not.
  if (record.target_archived) return 'archived_target';
  return 'expired';
}

interface Props {
  /** Active configs, so engage can only target something that can be engaged. */
  readonly configs: readonly RoutingConfigSummaryRecord[];
}

export function EmergencyOverridePanel({ configs }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const query = useRoutingEmergencyOverride();
  const engage = useEngageRoutingEmergencyOverride();
  const disengage = useDisengageRoutingEmergencyOverride();
  const preflight = useRunRoutingConfigPreflight();

  const [draft, setDraft] = useState<EngageDraft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [disengageReason, setDisengageReason] = useState<string | null>(null);

  const record = query.data?.override ?? null;
  const status = record === null ? 'off' : overrideStatus(record);

  const configOptions = useMemo(
    () => configs
      .filter((config) => config.status === 'active')
      .map((config) => ({ value: config.id, label: config.name })),
    [configs],
  );

  const selectedConfigName = useMemo(
    () => configOptions.find((option) => option.value === draft?.config_id)?.label ?? '',
    [configOptions, draft?.config_id],
  );

  /**
   * The preflight is run from inside this dialog rather than being pasted in.
   * The gateway requires the token, and an operator hunting for one in another
   * panel mid-incident is exactly when it gets skipped or reused stale.
   */
  const handleRunPreflight = async (): Promise<void> => {
    if (draft === null || draft.config_id.length === 0) return;
    try {
      const report = await preflight.mutateAsync({ configId: draft.config_id });
      setDraft((current) => current === null
        ? current
        : { ...current, preflight_token: report.preflight_token });
      toast.info(t('routing_dr.override.preflight_ran', {
        unreachable: report.unreachable_orgs.length,
      }));
    } catch (error) {
      setDraft((current) => current === null ? current : { ...current, preflight_token: null });
      toast.error(coerceErrorMessage(error));
    }
  };

  const handleEngageSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (draft === null) return;
    if (draft.config_id.length === 0) {
      toast.error(t('routing_dr.override.error_config_required'));
      return;
    }
    if (draft.reason.trim().length === 0) {
      toast.error(t('routing_dr.override.error_reason_required'));
      return;
    }
    if (draft.preflight_token === null) {
      toast.error(t('routing_dr.override.error_preflight_required'));
      return;
    }
    // The form does not submit; it opens the second confirmation. There is no
    // smaller unit of this action to undo, so the last gate names the config.
    setConfirming(true);
  };

  const handleEngageConfirm = async (): Promise<void> => {
    if (draft === null || draft.preflight_token === null) return;
    try {
      const response = await engage.mutateAsync({
        config_id: draft.config_id,
        reason: draft.reason.trim(),
        preflight_token: draft.preflight_token,
        ...(expiresInHours(draft.expiry_hours) === undefined
          ? {}
          : { expires_in_hours: expiresInHours(draft.expiry_hours)! }),
      });
      setConfirming(false);
      setDraft(null);
      toast.success(t(status === 'off'
        ? 'routing_dr.override.engaged_toast'
        : 'routing_dr.override.repointed_toast', {
        config: response.override.config_name ?? response.override.config_id ?? '',
      }));
    } catch (error) {
      setConfirming(false);
      toast.error(coerceErrorMessage(error));
    }
  };

  const handleDisengageConfirm = async (): Promise<void> => {
    const reason = (disengageReason ?? '').trim();
    if (reason.length === 0) {
      toast.error(t('routing_dr.override.error_reason_required'));
      return;
    }
    try {
      await disengage.mutateAsync({ reason });
      setDisengageReason(null);
      toast.success(t('routing_dr.override.disengaged_toast'));
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  };

  return (
    <>
      <Panel
        caption={t('routing_dr.override.caption')}
        testId="routing-dr-override-panel"
        padded
        // `bad` framing while in force, so the engaged state is legible from
        // the page's peripheral vision rather than only on inspection.
        className={status === 'in_force'
          ? 'border-bad-line bg-bad-dim/40'
          : status === 'expired' || status === 'archived_target'
            ? 'border-warn-line bg-warn-dim/40'
            : undefined}
        actions={
          // Engage is offered in every state, not only `off`. Both the gateway
          // handler and the DR runbook document repointing an engaged override
          // as a single write specifically so an operator does not have to
          // disengage first — which would drop the whole fleet back onto the
          // failing ladder mid-incident. Offering it only when `off` forced
          // exactly that sequence, and blocked the natural remedy for
          // `archived_target`, where the fix is to repoint onto a live config.
          <>
            <Button
              variant="danger"
              onClick={() => setDraft(EMPTY_DRAFT)}
              data-testid="routing-dr-override-open"
            >
              {t(status === 'off'
                ? 'routing_dr.override.engage_action'
                : 'routing_dr.override.repoint_action')}
            </Button>
            {status === 'off' ? null : (
              <Button
                variant="primary"
                onClick={() => setDisengageReason('')}
                data-testid="routing-dr-override-disengage-open"
              >
                {t('routing_dr.override.disengage_action')}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-3" data-testid="routing-dr-override-state">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={status === 'in_force'
              ? 'bad'
              : status === 'off' ? 'neutral' : 'warn'}>
              {t(`routing_dr.override.status_${status}`)}
            </Chip>
            {record !== null && record.config_id !== null ? (
              <span className="font-mono text-dense text-paper-mid">
                {record.config_name ?? record.config_id}
              </span>
            ) : null}
          </div>

          {status === 'off' ? (
            <p className="m-0 text-dense leading-5 text-paper-low">
              {t('routing_dr.override.idle_body')}
            </p>
          ) : (
            <div className="flex flex-col gap-1.5 text-dense leading-5 text-paper-mid">
              <p className="m-0" role="alert" data-testid="routing-dr-override-blast-radius">
                {t(`routing_dr.override.${status}_body`)}
              </p>
              {record?.reason !== null && record?.reason !== undefined ? (
                <p className="m-0 text-paper-low">
                  {t('routing_dr.override.reason_label')}: {record.reason}
                </p>
              ) : null}
              <p className="m-0 text-paper-low">
                {t('routing_dr.override.engaged_by_label')}: {record?.engaged_by ?? '—'}
                {record?.engaged_at === null || record?.engaged_at === undefined
                  ? null
                  : ` · ${formatLocalTimestamp(record.engaged_at)}`}
              </p>
              <p className="m-0 text-paper-low" data-testid="routing-dr-override-expiry">
                {t('routing_dr.override.expires_label')}:{' '}
                {record?.expires_at === null || record?.expires_at === undefined
                  ? t('routing_dr.override.expires_never')
                  : formatLocalTimestamp(record.expires_at)}
              </p>
            </div>
          )}
        </div>
      </Panel>

      <Dialog
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDraft(null);
            setConfirming(false);
          }
        }}
        title={t(status === 'off'
          ? 'routing_dr.override.engage_title'
          : 'routing_dr.override.repoint_title')}
        description={t(status === 'off'
          ? 'routing_dr.override.engage_description'
          : 'routing_dr.override.repoint_description', {
          current: record?.config_name ?? record?.config_id ?? '',
        })}
        closeLabel={t('common.close')}
        testId="routing-dr-override-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)} disabled={engage.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              type="submit"
              form="routing-dr-override-form"
              loading={engage.isPending}
              data-testid="routing-dr-override-save"
            >
              {t(status === 'off'
                ? 'routing_dr.override.engage_action'
                : 'routing_dr.override.repoint_action')}
            </Button>
          </>
        }
      >
        {draft === null ? null : (
          <form
            id="routing-dr-override-form"
            className="flex flex-col gap-4"
            onSubmit={handleEngageSubmit}
            data-testid="routing-dr-override-form"
          >
            <p
              className="m-0 rounded-md border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense leading-5 text-bad-bright"
              role="alert"
              data-testid="routing-dr-override-warning"
            >
              {t('routing_dr.override.blast_radius_warning')}
            </p>

            <FormField
              label={t('routing_dr.override.config_label')}
              fieldId="routing-dr-override-config"
              required
            >
              <Select
                id="routing-dr-override-config"
                value={draft.config_id.length === 0 ? undefined : draft.config_id}
                onValueChange={(value) => setDraft((current) => current === null
                  ? current
                  // Changing the target invalidates the token: it was computed
                  // against the config that was selected when it was run.
                  : { ...current, config_id: value, preflight_token: null })}
                options={configOptions}
                placeholder={t('routing_dr.override.config_placeholder')}
                testId="routing-dr-override-config"
              />
            </FormField>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => { void handleRunPreflight(); }}
                  disabled={draft.config_id.length === 0 || preflight.isPending}
                  loading={preflight.isPending}
                  data-testid="routing-dr-override-preflight"
                >
                  {t('routing_dr.override.preflight_action')}
                </Button>
                <Chip tone={draft.preflight_token === null ? 'warn' : 'ok'}>
                  {t(draft.preflight_token === null
                    ? 'routing_dr.override.preflight_missing'
                    : 'routing_dr.override.preflight_ready')}
                </Chip>
              </div>
              <p className="m-0 text-dense leading-5 text-paper-low">
                {t('routing_dr.override.preflight_hint')}
              </p>
            </div>

            <FormField
              label={t('routing_dr.override.reason_label')}
              fieldId="routing-dr-override-reason"
              required
              hint={t('routing_dr.override.reason_hint')}
            >
              <Textarea
                id="routing-dr-override-reason"
                rows={3}
                maxLength={MAX_SWITCH_REASON_LENGTH}
                value={draft.reason}
                onChange={(event) => {
                  const reason = event.currentTarget.value;
                  setDraft((current) => current === null ? current : { ...current, reason });
                }}
                data-testid="routing-dr-override-reason"
              />
            </FormField>

            <FormField
              label={t('routing_dr.override.expiry_label')}
              fieldId="routing-dr-override-expiry"
              hint={t('routing_dr.override.expiry_hint')}
            >
              <Select
                id="routing-dr-override-expiry"
                value={draft.expiry_hours}
                onValueChange={(value) => setDraft((current) => current === null
                  ? current
                  : { ...current, expiry_hours: value })}
                options={[
                  ...OVERRIDE_EXPIRY_HOURS_OPTIONS.map((hours) => ({
                    value: String(hours),
                    label: t('routing_dr.override.expiry_hours', { hours }),
                  })),
                  { value: '', label: t('routing_dr.override.expiry_none') },
                ]}
                testId="routing-dr-override-expiry-select"
              />
            </FormField>

            <p className="m-0 text-dense leading-5 text-paper-low">
              {t('routing_dr.propagation.hint', {
                seconds: ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
              })}
            </p>
          </form>
        )}
      </Dialog>

      {/*
        Second confirmation. Type-to-confirm on the config name, because the
        preceding form is the only place the operator saw which config this is
        and the whole fleet moves onto it.
      */}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t(status === 'off'
          ? 'routing_dr.override.confirm_title'
          : 'routing_dr.override.repoint_confirm_title')}
        body={t(status === 'off'
          ? 'routing_dr.override.confirm_body'
          : 'routing_dr.override.repoint_confirm_body', {
          current: record?.config_name ?? record?.config_id ?? '',
          next: selectedConfigName,
        })}
        confirmLabel={t(status === 'off'
          ? 'routing_dr.override.confirm_action'
          : 'routing_dr.override.repoint_confirm_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        typeToConfirm={selectedConfigName}
        typeToConfirmLabel={t('routing_dr.override.confirm_type_label')}
        loading={engage.isPending}
        onConfirm={() => { void handleEngageConfirm(); }}
        testId="routing-dr-override-confirm"
      />

      <Dialog
        open={disengageReason !== null}
        onOpenChange={(open) => {
          if (!open) setDisengageReason(null);
        }}
        title={t('routing_dr.override.disengage_title')}
        description={t('routing_dr.override.disengage_description')}
        closeLabel={t('common.close')}
        testId="routing-dr-override-disengage-dialog"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDisengageReason(null)}
              disabled={disengage.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => { void handleDisengageConfirm(); }}
              loading={disengage.isPending}
              data-testid="routing-dr-override-disengage-save"
            >
              {t('routing_dr.override.disengage_action')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <FormField
            label={t('routing_dr.override.reason_label')}
            fieldId="routing-dr-override-disengage-reason"
            required
          >
            <Textarea
              id="routing-dr-override-disengage-reason"
              rows={3}
              maxLength={MAX_SWITCH_REASON_LENGTH}
              value={disengageReason ?? ''}
              onChange={(event) => setDisengageReason(event.currentTarget.value)}
              data-testid="routing-dr-override-disengage-reason"
            />
          </FormField>
          <p className="m-0 text-dense leading-5 text-paper-low">
            {t('routing_dr.override.disengage_hint')}
          </p>
        </div>
      </Dialog>
    </>
  );
}
